import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppConfig } from "../../../AppConfig";
import { PageRoutes } from "../../../constants/routes";
import { CampaignManager } from "../../../core/storage/CampaignManager";
import { LocalStorageManager } from "../../../core/storage/LocalStorageManager";
import { appActions } from "../../../ducks/app";
import { campaignActions, campaignSelectors } from "../../../ducks/campaign";
import { Campaign } from "../../../ducks/campaign/types";
import { AppCoupon, couponActions } from "../../../ducks/coupon";
import { MenuInCart } from "../../../ducks/menu";
import { sortOrderActions } from "../../../ducks/sortOrder";
import { useNavigation } from "../../../hooks/useNavigation";

export const useCampaignSelection = () => {
  const dispatch = useDispatch();
  const { navigateToPage } = useNavigation();
  const activeCampaigns = useSelector(campaignSelectors.sortedActiveCampaigns);

  const menusInCart = LocalStorageManager.Instance.getObject<MenuInCart[]>(
    AppConfig.Instance.LocalStorageKey["menusInCart"],
  );

  useEffect(() => {
    dispatch(sortOrderActions.getSortOrder.started({}));
    // キャンペーンがなければホームに戻る
    if (activeCampaigns.length === 0) {
      navigateToPage(PageRoutes.HOME, false);
    }
  }, []);

  const handleClearLocalMenuData = useCallback(() => {
    const keysToRemove = [
      "menusInCart",
      "appliedCoupons",
      "selectedAppCoupons",
      "unselectedAppCoupons",
      "appToken",
    ];
    keysToRemove.forEach((key) => {
      LocalStorageManager.Instance.removeObject(
        AppConfig.Instance.LocalStorageKey[key],
      );
    });
  }, []);

  const handleCampaignClick = useCallback(
    (campaign: Campaign) => {
      const selectedAppCoupons =
        LocalStorageManager.Instance.getObject<AppCoupon[]>(
          AppConfig.Instance.LocalStorageKey["selectedAppCoupons"],
        ) ?? [];

      const hasMenusToReset =
        (menusInCart?.length ?? 0) > 0 || selectedAppCoupons.length > 0;

      const proceedWithCampaign = () => {
        LocalStorageManager.Instance.saveObject(
          AppConfig.Instance.LocalStorageKey["campaign"],
          campaign,
        );
        CampaignManager.setCampaignMode(campaign.type);
        dispatch(campaignActions.markSelectionMade());
        navigateToPage(PageRoutes.HOME, false);
      };

      if (hasMenusToReset) {
        dispatch(
          appActions.showModal.started({
            title:
              "現在のカートとクーポンが\nリセットされますが\nよろしいですか？",
            nextButtonLabel: "進む",
            closeButtonLabel: "通常の注文に戻る",
            handleNext: () => {
              dispatch(appActions.closeModal());
              dispatch(couponActions.resetToInitialState());
              handleClearLocalMenuData();
              proceedWithCampaign();
            },
            handleClose: () => {
              dispatch(appActions.closeModal());
            },
          }),
        );
        return;
      }
      proceedWithCampaign();
    },
    [dispatch, handleClearLocalMenuData, menusInCart, navigateToPage],
  );

  const handleRegularOrderClick = useCallback(() => {
    const hasCampaignMenus = (menusInCart?.length ?? 0) > 0;

    const proceedWithRegular = () => {
      LocalStorageManager.Instance.removeObject(
        AppConfig.Instance.LocalStorageKey["campaign"],
      );
      CampaignManager.clearCampaignMode();
      dispatch(campaignActions.markSelectionMade());
      navigateToPage(PageRoutes.HOME, false, { isRegular: true });
    };

    if (hasCampaignMenus) {
      dispatch(
        appActions.showModal.started({
          title:
            "現在のカートとクーポンが\nリセットされますが\nよろしいですか？",
          nextButtonLabel: "通常の注文に進む",
          closeButtonLabel: "戻る",
          handleNext: () => {
            dispatch(appActions.closeModal());
            handleClearLocalMenuData();
            proceedWithRegular();
          },
          handleClose: () => {
            dispatch(appActions.closeModal());
          },
        }),
      );
      return;
    }
    proceedWithRegular();
  }, [dispatch, handleClearLocalMenuData, menusInCart, navigateToPage]);

  return {
    activeCampaigns,
    handleCampaignClick,
    handleRegularOrderClick,
  };
};
